import * as React from "react";
import { useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { useDispatch, useSelector } from "react-redux";

import {
  cleanAthenaQuery,
  getAthenaColumns,
  getAthenaDatabases,
  getAthenaHistory,
  getAthenaTables,
  pollAthenaQuery,
  startAthenaQuery,
} from "../../redux/dynamo_db/actions";
import { downloadBulkResult } from "../../redux/global/reducer";
import {
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import getUserProfiles from "../../tokenUtils";
import { MaterialSymbol } from "@datalake/towercontrol-banner";
import "./AthenaQuery.css";

const OPERATORS = ["=", "!=", ">", "<", ">=", "<=", "LIKE"];
const POLL_INTERVAL_MS = 2000;

function getUserInfo() {
  try {
    const samlToken = getLocalStorageSAMLToken();
    return extractUserInfoFromToken(samlToken);
  } catch (e) {
    return { username: "anonymous", email: "" };
  }
}

function quoteValue(value) {
  // Every column in the datalake tables is typed as a string, so all values are
  // quoted as string literals — including purely numeric ones.
  return `'${String(value).replace(/'/g, "''")}'`;
}

export default function AthenaQuery() {
  const dispatch = useDispatch();
  const pollRef = useRef(null);
  const profiles = getUserProfiles() || [];
  const isAdmin =
    profiles.includes("vic-qa-admin") || profiles.includes("vic-prod-admin");

  const databases = useSelector((state) => state.athenaDatabases || []);
  const tables = useSelector((state) => state.athenaTables || []);
  const columns = useSelector((state) => state.athenaColumns || []);
  const queryResult = useSelector((state) => state.athenaQueryResult);
  const history = useSelector((state) => state.athenaHistory || []);

  const [tab, setTab] = React.useState(0);
  const [mode, setMode] = React.useState("builder"); // builder | sql
  const [database, setDatabase] = React.useState("");
  const [table, setTable] = React.useState("");
  const [selectedColumns, setSelectedColumns] = React.useState([]);
  // Filters are organised in groups. Conditions inside a group are joined by the
  // group's own connector; groups are joined by `groupConnector`. Each group is
  // wrapped in parentheses, so the builder can express e.g.
  //   ("a" = 1 OR "b" = 2) AND ("c" = 3).
  const [filterGroups, setFilterGroups] = React.useState([]);
  const [groupConnector, setGroupConnector] = React.useState("AND");
  const [limit, setLimit] = React.useState(100);
  const [sqlText, setSqlText] = React.useState("");
  const [running, setRunning] = React.useState(false);
  const [localError, setLocalError] = React.useState(null);

  useEffect(() => {
    dispatch(getAthenaDatabases());
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [dispatch]);

  const handleSelectDatabase = (value) => {
    setDatabase(value);
    setTable("");
    setSelectedColumns([]);
    setFilterGroups([]);
    dispatch(getAthenaTables(value));
  };

  const handleSelectTable = (value) => {
    setTable(value);
    setSelectedColumns([]);
    setFilterGroups([]);
    dispatch(getAthenaColumns(database, value));
  };

  const newCondition = () => ({ column: "", operator: "=", value: "" });

  const addGroup = () => {
    setFilterGroups([
      ...filterGroups,
      { connector: "AND", conditions: [newCondition()] },
    ]);
  };

  const removeGroup = (gi) => {
    setFilterGroups(filterGroups.filter((_, i) => i !== gi));
  };

  const updateGroupConnector = (gi, value) => {
    setFilterGroups(
      filterGroups.map((g, i) => (i === gi ? { ...g, connector: value } : g)),
    );
  };

  const addCondition = (gi) => {
    setFilterGroups(
      filterGroups.map((g, i) =>
        i === gi ? { ...g, conditions: [...g.conditions, newCondition()] } : g,
      ),
    );
  };

  const updateCondition = (gi, ci, key, value) => {
    setFilterGroups(
      filterGroups.map((g, i) =>
        i === gi
          ? {
              ...g,
              conditions: g.conditions.map((c, j) =>
                j === ci ? { ...c, [key]: value } : c,
              ),
            }
          : g,
      ),
    );
  };

  const removeCondition = (gi, ci) => {
    setFilterGroups(
      filterGroups.map((g, i) =>
        i === gi
          ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) }
          : g,
      ),
    );
  };

  const buildSql = () => {
    const cols =
      selectedColumns.length > 0
        ? selectedColumns.map((c) => `"${c}"`).join(", ")
        : "*";
    let sql = `SELECT ${cols} FROM "${database}"."${table}"`;
    const groupClauses = filterGroups
      .map((group) => {
        const conditions = group.conditions
          .filter((c) => c.column && c.value !== "")
          .map((c) => `"${c.column}" ${c.operator} ${quoteValue(c.value)}`);
        if (conditions.length === 0) return null;
        const joined = conditions.join(` ${group.connector} `);
        // Wrap multi-condition groups in parentheses so the outer connector
        // binds correctly; single conditions need no parentheses.
        return conditions.length > 1 ? `(${joined})` : joined;
      })
      .filter(Boolean);
    if (groupClauses.length > 0) {
      sql += ` WHERE ${groupClauses.join(` ${groupConnector} `)}`;
    }
    if (limit) {
      sql += ` LIMIT ${parseInt(limit, 10)}`;
    }
    return sql;
  };

  const poll = async (queryExecutionId, requestUser) => {
    const acted = await dispatch(
      pollAthenaQuery(queryExecutionId, requestUser),
    );
    const res = acted?.athenaQueryResult;
    if (!res) {
      setRunning(false);
      setLocalError("Failed to fetch query status.");
      return;
    }
    if (res.status === "QUEUED" || res.status === "RUNNING") {
      pollRef.current = setTimeout(
        () => poll(queryExecutionId, requestUser),
        POLL_INTERVAL_MS,
      );
    } else {
      setRunning(false);
      if (res.status !== "SUCCEEDED") {
        setLocalError(res.error || `Query ${res.status.toLowerCase()}.`);
      }
    }
  };

  const handleRun = async () => {
    setLocalError(null);
    dispatch(cleanAthenaQuery());

    if (!database) {
      setLocalError("Please select a database.");
      return;
    }
    let query;
    if (mode === "sql") {
      query = sqlText.trim();
      if (!query) {
        setLocalError("Please enter a SQL query.");
        return;
      }
    } else {
      if (!table) {
        setLocalError("Please select a table.");
        return;
      }
      query = buildSql();
    }

    const { username, email } = getUserInfo();
    setRunning(true);
    const acted = await dispatch(
      startAthenaQuery({
        database,
        query,
        request_user: username,
        email,
      }),
    );
    const start = acted?.athenaQueryStart;
    if (!start?.query_execution_id) {
      setRunning(false);
      setLocalError(
        acted?.errors?.error || "Failed to start the query. Check your SQL.",
      );
      return;
    }
    poll(start.query_execution_id, username);
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      downloadBulkResult(blob, filename);
    } catch (error) {
      console.error("Download failed:", error);
      setLocalError("Download failed. The result file may have expired.");
    }
  };

  const handleOpenHistory = () => {
    const { username } = getUserInfo();
    dispatch(getAthenaHistory(username));
  };

  const selectedDatabaseObject = databases.find((d) => d.name === database);

  // Tables are aggregated by project (the owning pipeline, derived backend-side
  // from each table's S3 location) so the dropdown is grouped under a subheader
  // per project. When no table carries a project we fall back to a flat list.
  const hasProjects = tables.some((t) => t.project);
  const tablesByProject = React.useMemo(() => {
    const groups = {};
    tables.forEach((t) => {
      const project = t.project || "Other";
      (groups[project] = groups[project] || []).push(t);
    });
    return Object.keys(groups)
      .sort()
      .map((project) => ({
        project,
        tables: groups[project]
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [tables]);

  const resultColumns = queryResult?.columns || [];
  const resultRows = queryResult?.rows || [];

  return (
    <React.Fragment>
      <Typography variant="h6" className="athena-title">
        Athena Query
      </Typography>
      <Tabs
        value={tab}
        onChange={(e, value) => {
          setTab(value);
          if (value === 1) handleOpenHistory();
        }}
        style={{ marginBottom: "16px" }}
      >
        <Tab label="Query" />
        <Tab label="History" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box className="athena-controls">
            <FormControl
              variant="standard"
              style={{ minWidth: 280 }}
              size="small"
            >
              <InputLabel>Database</InputLabel>
              <Select
                value={database}
                onChange={(e) => handleSelectDatabase(e.target.value)}
                renderValue={(value) => value}
              >
                {databases.map((db) => (
                  <MenuItem key={db.name} value={db.name}>
                    <ListItemText
                      primary={db.name}
                      secondary={db.description || "No description"}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isAdmin && (
              <Tabs
                value={mode}
                onChange={(e, value) => setMode(value)}
                className="athena-mode-tabs"
              >
                <Tab value="builder" label="Builder" />
                <Tab value="sql" label="SQL editor" />
              </Tabs>
            )}
          </Box>

          {selectedDatabaseObject?.description && (
            <Typography variant="body2" className="athena-db-description">
              {selectedDatabaseObject.description}
            </Typography>
          )}

          {mode === "builder" && (
            <Box className="athena-builder">
              <FormControl
                variant="standard"
                style={{ minWidth: 240 }}
                size="small"
                disabled={!database}
              >
                <InputLabel>Table</InputLabel>
                <Select
                  value={table}
                  onChange={(e) => handleSelectTable(e.target.value)}
                >
                  {hasProjects
                    ? tablesByProject.flatMap((group) => [
                        <ListSubheader key={`project-${group.project}`}>
                          {group.project}
                        </ListSubheader>,
                        ...group.tables.map((t) => (
                          <MenuItem key={t.name} value={t.name}>
                            <ListItemText
                              primary={t.name}
                              secondary={t.description || "No description"}
                            />
                          </MenuItem>
                        )),
                      ])
                    : tables.map((t) => (
                        <MenuItem key={t.name} value={t.name}>
                          <ListItemText
                            primary={t.name}
                            secondary={t.description || "No description"}
                          />
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>

              <FormControl
                style={{ minWidth: 280, marginLeft: 16 }}
                size="small"
                disabled={!table}
              >
                <InputLabel>Columns (default: all)</InputLabel>
                <Select
                  multiple
                  value={selectedColumns}
                  onChange={(e) => setSelectedColumns(e.target.value)}
                  input={<OutlinedInput label="Columns (default: all)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {columns.map((col) => (
                    <MenuItem key={col.name} value={col.name}>
                      <ListItemText
                        primary={col.name}
                        secondary={
                          col.comment
                            ? `${col.type} — ${col.comment}`
                            : col.type
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box className="athena-filters">
                <Box className="athena-filters-header">
                  <Typography variant="subtitle2">Filters</Typography>
                  {filterGroups.length > 1 && (
                    <FormControl variant="standard" size="small">
                      <Select
                        value={groupConnector}
                        onChange={(e) => setGroupConnector(e.target.value)}
                      >
                        <MenuItem value="AND">AND between groups</MenuItem>
                        <MenuItem value="OR">OR between groups</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {filterGroups.map((group, gi) => (
                  <React.Fragment key={gi}>
                    {gi > 0 && (
                      <Typography
                        variant="caption"
                        className="athena-group-connector"
                      >
                        {groupConnector}
                      </Typography>
                    )}
                    <Box className="athena-filter-group">
                      <Box className="athena-filters-header">
                        {group.conditions.length > 1 && (
                          <FormControl variant="standard" size="small">
                            <Select
                              value={group.connector}
                              onChange={(e) =>
                                updateGroupConnector(gi, e.target.value)
                              }
                            >
                              <MenuItem value="AND">AND</MenuItem>
                              <MenuItem value="OR">OR</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        <Tooltip title="Add condition">
                          <IconButton
                            onClick={() => addCondition(gi)}
                            size="small"
                          >
                            <MaterialSymbol name="add" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove group">
                          <IconButton
                            onClick={() => removeGroup(gi)}
                            size="small"
                          >
                            <MaterialSymbol name="delete" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {group.conditions.map((condition, ci) => (
                        <Box key={ci} className="athena-filter-row">
                          <FormControl
                            variant="standard"
                            size="small"
                            style={{ minWidth: 200 }}
                          >
                            <InputLabel>Column</InputLabel>
                            <Select
                              value={condition.column}
                              onChange={(e) =>
                                updateCondition(
                                  gi,
                                  ci,
                                  "column",
                                  e.target.value,
                                )
                              }
                            >
                              {columns.map((col) => (
                                <MenuItem key={col.name} value={col.name}>
                                  {col.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl variant="standard" size="small">
                            <Select
                              value={condition.operator}
                              onChange={(e) =>
                                updateCondition(
                                  gi,
                                  ci,
                                  "operator",
                                  e.target.value,
                                )
                              }
                            >
                              {OPERATORS.map((op) => (
                                <MenuItem key={op} value={op}>
                                  {op}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            variant="standard"
                            label="Value"
                            value={condition.value}
                            onChange={(e) =>
                              updateCondition(gi, ci, "value", e.target.value)
                            }
                          />
                          <IconButton
                            onClick={() => removeCondition(gi, ci)}
                            size="small"
                          >
                            <MaterialSymbol name="delete" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </React.Fragment>
                ))}

                <Button
                  onClick={addGroup}
                  disabled={!table}
                  size="small"
                  startIcon={<MaterialSymbol name="add" />}
                  style={{ alignSelf: "flex-start" }}
                >
                  Add filter group
                </Button>
              </Box>

              <TextField
                variant="standard"
                label="Limit"
                type="number"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                style={{ maxWidth: 120 }}
              />

              <Box className="athena-generated-sql">
                <Typography variant="caption" color="textSecondary">
                  Generated SQL
                </Typography>
                <pre>{database && table ? buildSql() : ""}</pre>
              </Box>
            </Box>
          )}

          {mode === "sql" && isAdmin && (
            <TextField
              label="SQL query"
              multiline
              minRows={6}
              fullWidth
              value={sqlText}
              onChange={(e) => setSqlText(e.target.value)}
              placeholder={`SELECT * FROM "${
                database || "database"
              }"."table" LIMIT 100`}
              style={{ marginTop: 16 }}
            />
          )}

          <Box style={{ marginTop: 16 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<MaterialSymbol name="play_arrow" />}
              onClick={handleRun}
              disabled={running}
            >
              Run query
            </Button>
          </Box>

          {running && (
            <Box style={{ marginTop: 16 }}>
              <LinearProgress />
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Running query…
              </Typography>
            </Box>
          )}

          {localError && (
            <Alert severity="error" style={{ marginTop: 16 }}>
              {localError}
            </Alert>
          )}

          {!running && queryResult?.status === "SUCCEEDED" && (
            <Box style={{ marginTop: 24 }}>
              <Box className="athena-results-header">
                <Typography variant="subtitle1">
                  {queryResult.total_count != null
                    ? `Showing ${resultRows.length} of ${queryResult.total_count} row${
                        queryResult.total_count === 1 ? "" : "s"
                      }`
                    : `Showing ${resultRows.length} row${
                        resultRows.length === 1 ? "" : "s"
                      }`}
                  {(queryResult.total_count == null ||
                    queryResult.total_count > resultRows.length) &&
                    ` (preview limited to ${queryResult.display_limit} — download for the full result)`}
                </Typography>
                {queryResult.download_url && (
                  <Button
                    variant="outlined"
                    startIcon={<MaterialSymbol name="download" />}
                    onClick={() =>
                      handleDownload(
                        queryResult.download_url,
                        `athena-${queryResult.query_execution_id}.csv`,
                      )
                    }
                  >
                    Download full result
                  </Button>
                )}
              </Box>
              <Box className="athena-results-table">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {resultColumns.map((col) => (
                        <TableCell key={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultRows.map((row, i) => (
                      <TableRow key={i} hover>
                        {resultColumns.map((col) => (
                          <TableCell key={col}>{row[col]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenHistory}
            style={{ marginBottom: 16 }}
          >
            Reload
          </Button>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started at</TableCell>
                <TableCell>Database</TableCell>
                <TableCell>Query</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rows</TableCell>
                <TableCell>Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow key={item.query_execution_id} hover>
                  <TableCell>{item.started_at}</TableCell>
                  <TableCell>{item.database}</TableCell>
                  <TableCell className="athena-history-query">
                    <Tooltip title={item.query || ""}>
                      <span>{item.query}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell>
                    {item.row_count == null || item.row_count < 0
                      ? "—"
                      : item.row_count}
                  </TableCell>
                  <TableCell>
                    {item.download_url && (
                      <IconButton
                        onClick={() =>
                          handleDownload(
                            item.download_url,
                            `athena-${item.query_execution_id}.csv`,
                          )
                        }
                      >
                        <MaterialSymbol name="download" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </React.Fragment>
  );
}
